import React, { useRef } from "react";
import PropTypes from "prop-types";
import { Icon, CircularProgress } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

function SoftImageInput({ label, image, onChange, onClear, loading, sx }) {
    const fileInputRef = useRef(null);

    const handleBoxClick = () => {
        if (loading) return;
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            onChange(event);
        }
    };

    return (
        <SoftBox width="100%" sx={sx}>
            {label && (
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                        {label}
                    </SoftTypography>
                </SoftBox>
            )}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: "none" }}
            />
            <SoftBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                height="220px"
                border="2px dashed #d2d6da"
                borderRadius="20px"
                cursor={loading ? "default" : "pointer"}
                onClick={handleBoxClick}
                sx={{
                    backgroundColor: "#fbfcfe",
                    transition: "all 0.3s ease",
                    "&:hover": {
                        backgroundColor: loading ? "#fbfcfe" : "#f8f9fa",
                        borderColor: loading ? "#d2d6da" : "#67748e",
                        transform: loading ? "none" : "translateY(-2px)",
                    },
                    backgroundImage: image ? `url(${image})` : "none",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    position: "relative",
                    overflow: "hidden",
                    opacity: loading ? 0.7 : 1,
                }}
            >
                {loading && (
                    <SoftBox
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        zIndex={2}
                        bgcolor="rgba(255, 255, 255, 0.5)"
                    >
                        <CircularProgress size={40} color="info" />
                    </SoftBox>
                )}
                {!image && !loading && (
                    <SoftBox textAlign="center" p={3}>
                        <Icon fontSize="large" sx={{ color: "#adb5bd", mb: 1, fontSize: "48px !important" }}>
                            add_photo_alternate
                        </Icon>
                        <SoftTypography variant="button" color="secondary" fontWeight="medium" display="block">
                            Cliquez pour choisir une image
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text" fontWeight="regular">
                            PNG, JPG, JPEG (2MB max)
                        </SoftTypography>
                    </SoftBox>
                )}
                {image && !loading && (
                    <SoftBox
                        position="absolute"
                        top="15px"
                        right="15px"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClear();
                            fileInputRef.current.value = "";
                        }}
                    >
                        <SoftButton variant="gradient" color="error" size="small" iconOnly sx={{ minWidth: "24px", minHeight: "24px", width: "24px", height: "24px" }}>
                            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
                        </SoftButton>
                    </SoftBox>
                )}
            </SoftBox>
        </SoftBox>
    );
}

SoftImageInput.propTypes = {
    label: PropTypes.string,
    image: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    sx: PropTypes.objectOf(PropTypes.any),
};

export default SoftImageInput;
